import { useState, type CSSProperties } from 'react';
import { IconBack } from './components/icons';

// Account surface — identity (who am I) + a self-serve delete. Deliberately small: NOT settings, NOT billing.
// The delete reuses the existing DELETE /api/auth/account (soft-delete + 7-day grace); this component only
// renders the identity + a destructive two-step confirm and delegates the call to the parent (onDelete).
// No CV-content path here.

// Revenue P0 (P0-1) — bundle_credits_remaining rides along (auth/routes.ts's /me query, PR-pivot-4.5) so the
// checkout-success poll can detect a bundle purchase landing (credits appeared) without a separate endpoint.
export interface Me { account_id: string; email: string; applicant_legal_name: string | null; created_at: number | null; bundle_credits_remaining?: number }

export function Account({ me, onBack, onDelete }: { me: Me | null; onBack: () => void; onDelete: () => Promise<boolean> }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  async function doDelete() {
    setDeleting(true); setError('');
    const ok = await onDelete(); // parent makes the DELETE call + resets to the landing on success (unmounts us)
    if (!ok) { setDeleting(false); setError('Couldn’t delete your account just now — please try again.'); }
  }

  const created = me?.created_at ? new Date(me.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : null;

  return (
    <main style={S.main} data-testid="account">
      <button className="btn btn-ghost" style={S.back} onClick={onBack} data-testid="account-back"><IconBack aria-hidden="true" /> Back</button>
      <h1>Account</h1>

      <dl style={S.dl} data-testid="account-identity">
        <dt style={S.dt}>Email</dt>
        <dd style={S.dd} data-testid="account-email">{me?.email ?? '—'}</dd>
        {me?.applicant_legal_name ? (<><dt style={S.dt}>Name</dt><dd style={S.dd}>{me.applicant_legal_name}</dd></>) : null}
        {created ? (<><dt style={S.dt}>Member since</dt><dd className="tnum" style={S.dd}>{created}</dd></>) : null}
      </dl>

      <section style={S.danger} data-testid="account-delete-section">
        <h2 style={S.dangerH}>Delete account</h2>
        <p style={S.dangerP}>
          This deactivates your account — and all your CVs — immediately. There's a 7-day grace period: sign in
          again within 7 days to recover everything. After that, your account is queued for permanent removal from
          our active systems, and backups age out on a rolling schedule.
        </p>
        {!confirming ? (
          <button className="btn btn-secondary" style={S.dangerBtn} onClick={() => setConfirming(true)} data-testid="account-delete">Delete my account…</button>
        ) : (
          <div style={S.confirm} data-testid="account-delete-confirm">
            <p style={S.confirmP}><strong>Delete your account?</strong> You can still sign in within 7 days to cancel.</p>
            <button className="btn btn-primary" style={S.delConfirmBtn} onClick={doDelete} disabled={deleting} data-testid="account-delete-yes">{deleting ? 'Deleting…' : 'Yes, delete my account'}</button>
            <button className="btn btn-secondary" onClick={() => setConfirming(false)} disabled={deleting} data-testid="account-delete-cancel">Cancel</button>
          </div>
        )}
        {error && <p style={S.err} data-testid="account-delete-error">{error}</p>}
      </section>
    </main>
  );
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 560, margin: '40px auto', padding: '0 var(--space-4)', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-body)' },
  back: { marginLeft: 0, paddingLeft: 0, fontSize: 'var(--fs-small)', marginBottom: 'var(--space-2)' },
  dl: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--space-2) var(--space-4)', margin: 'var(--space-3) 0 28px' },
  dt: { color: 'var(--muted)', fontSize: 'var(--fs-body)' },
  dd: { margin: 0, color: 'var(--ink)' },
  danger: { border: '1px solid var(--rule)', borderRadius: 10, padding: 'var(--space-4)', background: 'var(--card)' },
  dangerH: { fontSize: 'var(--fs-h3)', margin: '0 0 var(--space-2)', color: 'var(--danger)' },
  dangerP: { fontSize: 'var(--fs-small)', color: 'var(--muted)', margin: '0 0 var(--space-3)' },
  dangerBtn: { color: 'var(--danger)', borderColor: 'var(--rule)' },
  confirm: { background: 'var(--danger-surface)', border: '1px solid var(--danger)', borderRadius: 8, padding: 'var(--space-3)' },
  confirmP: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-body)', color: 'var(--ink)' },
  delConfirmBtn: { background: 'var(--danger)', borderColor: 'var(--danger)' },
  err: { color: 'var(--danger)', marginTop: 'var(--space-3)', fontSize: 'var(--fs-small)' },
};
